import { DateRangePicker } from '@/design-system/components/dateRangePicker/DateRangePicker';
import { Dropdown } from '@/design-system/components/dropdown/Dropdown';
import { UniInput } from '@/design-system/components/inputs/UniInput';
import { TextCaption } from '@/design-system/components/typography';
import { useDebounce } from '@/utils/useDebounce.hook';
import { LayoutContext, ScreenSize } from 'contexts/LayoutContext';
import { View, SxProp, Pressable } from 'dripsy';
import moment from 'moment-timezone';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const DEFAULT_FILTERS = {
  provider: undefined,
  product: undefined,
  search: undefined,
  action: undefined,
  category: undefined,
  fromDate: undefined,
  toDate: undefined
};

export interface ActivityHistoryFilterParams {
  provider?: string;
  search?: string;
  product?: string;
  action?: 'SUBSCRIPTION' | 'REDEMPTION';
  category?: string;
  type?: string;
  fromDate?: Date;
  toDate?: Date;
}

interface ActivityHistoryFilterProps {
  categoriesFilters: Array<{ label: string; value: string }>;
  onFilterChange: (filter: ActivityHistoryFilterParams) => void;
}

export const ActivityHistoryFilter: React.FC<ActivityHistoryFilterProps> = ({ categoriesFilters, onFilterChange }) => {
  const { t } = useTranslation('earn');
  const layout = useContext(LayoutContext);
  const [filter, setFilter] = useState<ActivityHistoryFilterParams>(DEFAULT_FILTERS);
  const debouncedFilter = useDebounce(filter);

  function onChange(params: { name: string; value?: any }) {
    setFilter((prev) => ({ ...prev, [params.name]: params.value }));
  }

  function onResetFilter() {
    setFilter(DEFAULT_FILTERS);
  }

  useEffect(() => {
    onFilterChange(debouncedFilter);
  }, [debouncedFilter]);

  return (
    <View sx={{ ...styles.container, flexDirection: layout.screenSize === ScreenSize.DesktopFull ? 'row' : 'column' }}>
      <View sx={styles.containerLeft}>
        {/* Date Picker */}
        <View sx={{ flexDirection: 'row', alignItems: 'center', gap: '$5' }}>
          <DateRangePicker
            dateRangeProps={{ from: filter.fromDate ? moment(filter.fromDate) : null, to: filter.toDate ? moment(filter.toDate) : null }}
            onValueChange={(value) => setFilter((prev) => ({ ...prev, fromDate: value.from, toDate: value.to }))}
          />
          <Dropdown
            sx={{ height: 38 }}
            onValueChange={(value) => onChange({ name: 'category', value })}
            options={categoriesFilters}
            value={filter.category}
            prefix={t('earn-invest::glossary.type')}
            placeholder={t('earn-invest::glossary.all-types')}
          />
          <Pressable onPress={() => onResetFilter()}>
            <TextCaption sx={{ color: Object.values(filter).filter((item) => item).length === 0 ? '$disable' : '$textFilterActive' }}>
              {t('earn-invest::glossary.clear-filter')}
            </TextCaption>
          </Pressable>
        </View>
        {layout.screenSize !== ScreenSize.DesktopFull && (
          <UniInput
            value={filter.search}
            sx={{ width: '100%', height: 38 }}
            wrapperSx={{ flex: 1, flexGrow: 1, width: '100%' }}
            size="default"
            onValueChange={(value) => onChange({ name: 'search', value: value && value.toLowerCase() })}
            prefixIcon="search"
            placeholder={t('earn-invest::glossary.activity-history-search')}
          />
        )}
      </View>
      <View sx={{ flex: 1, flexDirection: 'row', justifyContent: 'flex-end', gap: '$3' }}>
        {/* Provider Dropdown */}
        {layout.screenSize === ScreenSize.DesktopFull && (
          <UniInput
            value={filter.search}
            sx={{ minWidth: 350, height: 38 }}
            size="default"
            onValueChange={(value) => onChange({ name: 'search', value: value && value.toUpperCase() })}
            prefixIcon="search"
            placeholder={t('earn-invest::glossary.activity-history-search')}
          />
        )}
      </View>
    </View>
  );
};

enum StyleName {
  container = 'container',
  containerLeft = 'containerLeft',
  containerRight = 'containerRight'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '$3',
    zIndex: 1
  },
  containerLeft: {
    flexDirection: 'row',
    gap: '$3',
    justifyContent: 'space-between',
    zIndex: 2
  },
  containerRight: {
    flex: 1,
    alignItems: 'flex-end'
  }
};
